import React from "react";
import { Check, RefreshCw, Tag, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	DropdownMenuCheckboxItem,
	DropdownMenuItem,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "../../ui/dropdown-menu";
import { isCredentialValid, tagHoursLeft } from "../../../utils/credential.utils";
import TagChip from "../TagChip/TagChip";

/** An org tag type offered in the submenu (consultant/customer/job taxonomy). */
interface TagTypeOption {
	key: string;
	name: { sv: string };
	color?: string | null;
	/** Hours a held tag stays valid (consultant tags only); unset = never expires. */
	validityDuration?: number;
}

/** A tag held by the entity; `expiresAt` only exists for consultant tags. */
interface AssignedTag {
	key: string;
	expiresAt?: string | null;
}

interface TagToggleSubMenuProps {
	/** Submenu trigger label. */
	label?: string;
	tagTypes: TagTypeOption[];
	assignedTags: AssignedTag[];
	/** Toggle the tag with the given key on the entity. */
	onToggle: (key: string) => void;
	/**
	 * Re-apply a held TIMED tag so its clock restarts. When both this and
	 * `onRemove` are given, a validly held timed tag renders as a nested menu
	 * (Förnya / Ta bort) instead of a checkbox — re-marking must never silently
	 * remove a prioritisation.
	 */
	onRenew?: (key: string) => void;
	/** Explicitly remove a held timed tag (see `onRenew`). */
	onRemove?: (key: string) => void;
}

/**
 * "Taggar" submenu for a shadcn/Radix DropdownMenu: one checkbox row per org
 * tag type, checked when the entity holds a valid (unexpired) entry for the
 * key, rendered as the tag's colored chip. Must be rendered inside a
 * DropdownMenuContent. Shared by the contact context menu (consultant tags)
 * and detail-page overflow menus (e.g. customer tags).
 *
 * Timed consultant tags (a type with `validityDuration`) that are currently
 * held show the hours left and open a nested Förnya / Ta bort menu when the
 * caller provides `onRenew` + `onRemove`.
 */
const TagToggleSubMenu: React.FC<TagToggleSubMenuProps> = ({
	label = "Taggar",
	tagTypes,
	assignedTags,
	onToggle,
	onRenew,
	onRemove,
}) => {
	const { t } = useTranslation();

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger>
				<Tag />
				<span>{label}</span>
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent className="w-64">
				{tagTypes.map((type) => {
					const held = assignedTags.find(
						(tag) => tag.key === type.key && isCredentialValid(tag.expiresAt)
					);
					const isTimed = !!type.validityDuration && type.validityDuration > 0;

					if (held && isTimed && onRenew && onRemove) {
						const hoursLeft = tagHoursLeft(held.expiresAt);

						return (
							<DropdownMenuSub key={type.key}>
								{/* Same indent + check mark as the checkbox rows so the list reads as one. */}
								<DropdownMenuSubTrigger className="relative pl-8">
									<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
										<Check className="h-4 w-4" />
									</span>
									<TagChip name={type.name.sv} color={type.color} size="sm" />
									{hoursLeft !== null && (
										<DropdownMenuShortcut className="tracking-normal text-muted-foreground">
											{t("consultantTag.hoursLeft", { count: hoursLeft })}
										</DropdownMenuShortcut>
									)}
								</DropdownMenuSubTrigger>
								<DropdownMenuSubContent className="w-56">
									<DropdownMenuItem onSelect={() => onRenew(type.key)}>
										<RefreshCw />
										<span>{t("consultantTag.renew", { hours: type.validityDuration })}</span>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => onRemove(type.key)}>
										<X />
										<span>{t("consultantTag.remove")}</span>
									</DropdownMenuItem>
								</DropdownMenuSubContent>
							</DropdownMenuSub>
						);
					}

					return (
						<DropdownMenuCheckboxItem
							key={type.key}
							checked={!!held}
							onSelect={(event) => {
								event.preventDefault();
								onToggle(type.key);
							}}
						>
							<TagChip name={type.name.sv} color={type.color} size="sm" />
						</DropdownMenuCheckboxItem>
					);
				})}
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	);
};

export default TagToggleSubMenu;
